import React from "react";
import { CustomTextField } from "./CustomTextField";
import { TextFieldProps } from "@mui/material";
import { MuiTextFieldProps } from "@mui/lab/internal/pickers/PureDateInput";

interface NumberFieldProps {
  name: string;
  label?: string;
  value: number | string | null | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  InputProps?: TextFieldProps["InputProps"];
  sx?: MuiTextFieldProps["sx"];

  max?: number;
  info?: string;
}

const sanitizeNumber = (value: string) => {
  if (value === "") return "";

  const digits = value.replace(/\D/g, "");

  if (digits === "") return "";

  // Convert 000 → 0, 0012 → 12
  return String(Number(digits));
};

const toDisplayValue = (value: string | number | null | undefined) => {
  if (value === 0 || value === "0") return "";
  return value ?? "";
};

export const MuiNumberField: React.FC<NumberFieldProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  disabled,
  placeholder = "0",
  InputProps,
  sx,

  max,
  info,
}) => {
  const [internalError, setInternalError] = React.useState<
    string | undefined
  >();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const sanitized = sanitizeNumber(raw);

    const numericValue = Number(sanitized);

    if (max !== undefined && numericValue > max) {
      setInternalError(`Maximum allowed value is ${max}`);
      return;
    }

    setInternalError(undefined);

    onChange({
      ...e,
      target: {
        ...e.target,
        name,
        value: sanitized,
      },
    });
  };

  React.useEffect(() => {
    setInternalError(undefined);
  }, [value]);

  return (
    <CustomTextField
      type="text"
      inputMode="numeric"
      name={name}
      label={label}
      fullWidth
      value={toDisplayValue(value)}
      placeholder={placeholder}
      error={!!error || !!internalError}
      helperText={error || internalError}
      onChange={handleChange}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      inputProps={{
        inputMode: "numeric", // ← mobile numeric keyboard
        pattern: "[0-9]*", // ← allow only digits
      }}
      sx={sx}
      InputProps={InputProps}
      info={info}
    />
  );
};
